/**
 * Admin API Service - handles all admin-related API calls
 */
import { apiService } from './apiService';
import type { ApiResponse } from '../types';
import type { AdminUser, AdminBlog, AdminUsersResponse, AdminBlogsResponse } from '../types/admin';
import { ENV } from '@/config/environment';

// Export types for use in other files
export type { AdminUser, AdminBlog, AdminUsersResponse, AdminBlogsResponse };

interface PaginationParams {
  page?: number;
  per_page?: number;
}

class AdminApiService {
  /**
   * Get all users in project (admin only)
   */
  async getUsers(params?: PaginationParams): Promise<ApiResponse<AdminUsersResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    
    const url = `/users/admin/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiService.get<AdminUsersResponse>(url);
  }

  /**
   * Approve a user (admin only)
   */
  async approveUser(userId: string): Promise<ApiResponse<AdminUser>> {
    return await apiService.post<AdminUser>(`/users/admin/users/${userId}/approve`);
  }

  /**
   * Update user info (admin only, cannot change password)
   */
  async updateUser(userId: string, userData: Partial<AdminUser>): Promise<ApiResponse<AdminUser>> {
    return await apiService.put<AdminUser>(`/users/admin/users/${userId}`, userData);
  }

  /**
   * Update user role (admin only)
   */
  async updateUserRole(userId: string, role: 'user' | 'admin'): Promise<ApiResponse<AdminUser>> {
    return await apiService.put<AdminUser>(`/users/admin/users/${userId}/role`, { role });
  }

  /**
   * Activate a user (admin only)
   */
  async activateUser(userId: string): Promise<ApiResponse<AdminUser>> {
    return await apiService.put<AdminUser>(`/users/admin/users/${userId}/activate`, {});
  }

  /**
   * Deactivate a user (admin only, non-admin users only)
   */
  async deactivateUser(userId: string): Promise<ApiResponse<AdminUser>> {
    return await apiService.put<AdminUser>(`/users/admin/users/${userId}/deactivate`, {});
  }

  /**
   * Update user status (activate/deactivate) - admin only
   */
  async updateUserStatus(userId: string, isActive: boolean): Promise<ApiResponse<AdminUser>> {
    if (isActive) {
      return await this.activateUser(userId);
    } else {
      return await this.deactivateUser(userId);
    }
  }

  /**
   * Delete a user (admin only, non-admin users only)
   */
  async deleteUser(userId: string): Promise<ApiResponse<null>> {
    return await apiService.delete<null>(`/users/admin/users/${userId}`);
  }

  /**
   * Generate a password-reset link for a user, to hand to them directly
   * (admin only). Registered under the admin_bp blueprint, not users_bp -
   * hence the different `/admin/users/...` path prefix from the methods
   * above.
   */
  async generatePasswordResetLink(userId: string): Promise<ApiResponse<{ reset_url: string; expires_in_hours: number }>> {
    return await apiService.post<{ reset_url: string; expires_in_hours: number }>(
      `/admin/users/${userId}/reset-password-link`
    );
  }

  /**
   * Get all blogs in project (admin only)
   */
  async getBlogs(params?: PaginationParams): Promise<ApiResponse<AdminBlogsResponse>> {
    if (ENV.app.localDevMode) {
      const { localDevAdminBlogs } = await import('@/features/blog/mockData');
      return {
        success: true,
        message: 'Local dev blog seed data loaded',
        data: {
          blogs: localDevAdminBlogs,
          total: localDevAdminBlogs.length,
        },
      };
    }

    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    
    const url = `/blogs/admin/blogs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiService.get<AdminBlogsResponse>(url);
  }

  /**
   * Archive a blog (admin only)
   */
  async archiveBlog(blogId: string): Promise<ApiResponse<AdminBlog>> {
    return await apiService.post<AdminBlog>(`/blogs/admin/blogs/${blogId}/archive`);
  }

  /**
   * Unarchive a blog (admin only)
   */
  async unarchiveBlog(blogId: string): Promise<ApiResponse<AdminBlog>> {
    return await apiService.post<AdminBlog>(`/blogs/admin/blogs/${blogId}/unarchive`);
  }

  /**
   * Moderate a blog (admin only)
   */
  async moderateBlog(blogId: string, action: 'approve' | 'reject' | 'flag', reason?: string): Promise<ApiResponse<{
    blog_id: string;
    action: string;
    status: string;
    moderated_at: string;
    reason?: string;
  }>> {
    return await apiService.post(`/blogs/admin/blogs/${blogId}/moderate`, {
      action,
      reason: reason || ''
    });
  }

  /**
   * Bulk user operations (admin only)
   */
  async bulkUserOperations(operation: 'activate' | 'deactivate' | 'delete', userIds: string[]): Promise<ApiResponse<{
    operation: string;
    total_users: number;
    success_count: number;
    failed_count: number;
    results: Array<{
      user_id: string;
      email: string;
      status: string;
      error?: string;
    }>;
  }>> {
    return await apiService.post(`/users/admin/users/bulk`, {
      operation,
      user_ids: userIds
    });
  }

  /**
   * Get system logs (admin only)
   */
  async getSystemLogs(params?: {
    limit?: number;
    offset?: number;
    event_type?: string;
    entity_type?: string;
    user_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<{
    logs: Array<{
      id: string;
      user_id: string | null;
      event_type: string;
      entity_type: string;
      entity_id: string | null;
      action: string;
      description: string;
      metadata: Record<string, any>;
      ip_address: string | null;
      user_agent: string | null;
      created_at: string | null;
    }>;
    pagination: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.event_type) queryParams.append('event_type', params.event_type);
    if (params?.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params?.user_id) queryParams.append('user_id', params.user_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    
    const url = `/admin/logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiService.get(url);
  }

  /**
   * Get client + server error reports for the Error Monitoring screen (admin only)
   */
  async getErrorReports(params?: {
    limit?: number;
    offset?: number;
    source?: 'client_error' | 'server_error';
  }): Promise<ApiResponse<{
    errors: Array<{
      id: string;
      event_type: 'client_error' | 'server_error';
      description: string;
      metadata: Record<string, any>;
      user_id: string | null;
      ip_address: string | null;
      created_at: string | null;
    }>;
    pagination: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
    summary: {
      client_errors_24h: number;
      server_errors_24h: number;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.source) queryParams.append('source', params.source);

    const url = `/admin/errors${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiService.get(url);
  }

  /**
   * Perform maintenance operations (admin only)
   */
  async performMaintenance(operation: 'clear_cache' | 'rebuild_indexes' | 'cleanup_old_data' | 'vacuum_database', params?: {
    retention_days?: number;
  }): Promise<ApiResponse<{
    operation: string;
    status: 'success' | 'partial' | 'error';
    message: string;
    details: Record<string, any>;
  }>> {
    return await apiService.post('/admin/maintenance', {
      operation,
      ...params
    });
  }

  /**
   * Get system statistics (admin only)
   */
  async getSystemStats(): Promise<ApiResponse<{
    users: {
      total: number;
      active: number;
      inactive: number;
    };
    blogs: {
      total: number;
      published: number;
      drafts: number;
    };
    sessions: {
      active: number;
    };
    system_health: {
      status: 'healthy' | 'warning' | 'degraded';
      issues: string[];
      timestamp: string;
    };
  }>> {
    return await apiService.get('/admin/stats');
  }

  /**
   * Get activity events (admin only)
   */
  async getActivityEvents(params?: {
    limit?: number;
    offset?: number;
    event_type?: string;
    entity_type?: string;
    user_id?: string;
  }): Promise<ApiResponse<{
    activities: Array<{
      id: string;
      user_id: string | null;
      event_type: string;
      entity_type: string;
      entity_id: string | null;
      action: string;
      description: string;
      metadata: Record<string, any>;
      ip_address: string | null;
      user_agent: string | null;
      created_at: string | null;
    }>;
    total: number;
    limit: number;
    offset: number;
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.event_type) queryParams.append('event_type', params.event_type);
    if (params?.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params?.user_id) queryParams.append('user_id', params.user_id);
    
    const url = `/users/admin/users/activity${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiService.get(url);
  }
}

export const adminApiService = new AdminApiService();
